import { useTranslation } from "react-i18next";
import { Caption, IconButton, Text, TouchableRipple } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import { millisecondsToMinutesAndSeconds } from "../AudioPlayer";
import Box from "../Box";
import { WaveformVisualizer } from "../WaveformVisualizer";

type Props = {
	icon: string;
	handleRecord: () => void;
	bgColor?: string;
	disabled?: boolean;
	handleDelete: () => void;
	showDelete?: boolean;
	isRecording: boolean;
	isPlaying: boolean;
	progress?: number;
	meterHistory?: number[];
	label?: string;
	onSeek?: (progress: number) => Promise<void>;
	isLoading?: boolean;
	currentTime?: number;
	totalTime?: number;
	isPlayingMode?: boolean;
};

const AudioRecorder = ({
	icon,
	handleRecord,
	bgColor,
	disabled = false,
	handleDelete,
	showDelete = false,
	isRecording,
	isPlaying,
	progress = 0,
	meterHistory = [],
	label,
	onSeek,
	isLoading = false,
	currentTime,
	totalTime,
	isPlayingMode,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const color = bgColor ?? theme.colors.primary;

	const showWaveform = isRecording || meterHistory.length > 0;

	const defaultLabel = t("recorder.tapToRecord");

	const formattedCurentTime = millisecondsToMinutesAndSeconds(currentTime ?? 0);
	const formattedTotalTime = millisecondsToMinutesAndSeconds(totalTime ?? 0);

	const progressText = `${formattedCurentTime} / ${formattedTotalTime}`;

	return (
		<TouchableRipple
			onPress={handleRecord}
			disabled={isPlayingMode}
			style={{
				width: "100%",
				cursor: isPlayingMode ? "auto" : "pointer",
				borderRadius: 8,
				borderWidth: 2,
				borderColor: isRecording ? color : theme.colors.secondaryContainer,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				padding={12}
				width="100%"
				gap={8}
			>
				{showDelete && (
					<IconButton
						icon="close"
						size={20}
						disabled={disabled}
						onPress={handleDelete}
						mode="contained"
						style={{
							margin: 0,
						}}
					/>
				)}
				{showWaveform ? (
					<Box flex={1}>
						<WaveformVisualizer
							mode={isRecording ? "recorder" : "default"}
							progress={isRecording ? 1 : progress}
							isPlaying={isPlaying || isRecording}
							activeColor={color}
							maxHeight={25}
							meterHistory={meterHistory}
							onSeek={onSeek}
							canSeek={!isRecording}
							cursor={isPlayingMode ? undefined : "pointer"}
						/>
					</Box>
				) : (
					<Caption
						style={{
							flex: 1,
						}}
					>
						{label || defaultLabel}
					</Caption>
				)}
				<Text
					variant="labelLarge"
					numberOfLines={1}
					adjustsFontSizeToFit
					style={{
						paddingHorizontal: 0,
						minWidth: 68,
						textAlign: "right",
					}}
				>
					{progressText}
				</Text>
				<IconButton
					icon={icon}
					loading={isLoading}
					disabled={disabled}
					mode="contained"
					containerColor={color}
					size={20}
					style={{
						margin: 0,
					}}
					onPress={handleRecord}
					iconColor={theme.colors.surface}
				/>
			</Box>
		</TouchableRipple>
	);
};
export default AudioRecorder;
